// Simulador de licitación con margen garantizado. Catering → Resumen → Simulador de licitación.
// Cotiza con el COSTO REAL de las recetas: food cost, margen al precio propuesto,
// precio garantizado para el margen objetivo bajo inflación de insumos, y el
// colchón de inflación que aguanta cada precio antes de entrar en pérdida.
import { Fragment, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Calculator, Plus, Trash2, Save, ShieldCheck, TrendingDown, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { fieldCls, num } from '@/features/inventario/shared';
import { cat, catRpc, money, SERVICIO_LABEL, SERVICIOS } from './shared';

interface Ciclo { id: string; nombre: string }
interface Fila { servicio: string; pax: string; ciclo_id: string; precio: string }
interface SrvRes { servicio: string; pax: number; costo_mp_racion: number; indirecto_racion: number; costo_total_racion: number; precio: number; margen_racion: number; margen_pct: number | null; food_cost_pct: number | null; precio_garantizado: number | null; colchon_inflacion_pct: number | null }
interface Res {
  por_servicio: SrvRes[];
  totales_dia: { ingreso: number; costo_mp: number; costo_total: number; margen: number; margen_pct: number | null; food_cost_pct: number | null; colchon_inflacion_pct: number | null };
  totales_periodo: { dias: number; ingreso: number; costo_total: number; margen: number };
  recomendacion: { margen_objetivo_pct: number; inflacion_colchon_pct: number; ingreso_garantizado_dia: number; margen_dia_a_precio_garantizado: number };
  escenarios: { inflacion_pct: number; costo_dia: number; margen_dia: number; margen_pct: number | null }[];
}
interface LicRow { id: string; codigo: string | null; nombre: string; sede: string | null; margen_pct: string | null; margen_dia: string | null }

export function LicitacionCatering() {
  const qc = useQueryClient();
  const [nombre, setNombre] = useState('');
  const [sede, setSede] = useState('');
  const [dias, setDias] = useState('30');
  const [indirecto, setIndirecto] = useState('0.70');
  const [margen, setMargen] = useState('25');
  const [inflacion, setInflacion] = useState('10');
  const [filas, setFilas] = useState<Fila[]>([{ servicio: 'almuerzo_1', pax: '', ciclo_id: '', precio: '' }]);
  const [res, setRes] = useState<Res | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const [abierto, setAbierto] = useState<string | null>(null);
  const [kpiOpen, setKpiOpen] = useState(false);
  const [escOpen, setEscOpen] = useState(false);

  const { data: ciclos = [] } = useQuery<Ciclo[]>({ queryKey: ['cat-ciclos-opt'], queryFn: () => cat.list<Ciclo>('ciclos', '?select=id,nombre&deleted_at=is.null&order=orden') });
  const { data: guardadas = [] } = useQuery<LicRow[]>({ queryKey: ['cat-licitaciones'], queryFn: () => catRpc<LicRow[]>('cat_licitaciones_listar') });

  const params = useMemo(() => ({
    nombre, sede, dias: parseInt(dias || '30', 10), indirecto_racion: parseFloat(indirecto || '0') || 0,
    margen_objetivo_pct: parseFloat(margen || '0') || 0, inflacion_pct: parseFloat(inflacion || '0') || 0,
    escenarios: [0, 5, 10, 20],
    servicios: filas.filter((f) => f.pax && f.precio).map((f) => ({
      servicio: f.servicio, pax: parseFloat(f.pax), precio: parseFloat(f.precio),
      ...(f.ciclo_id ? { ciclo_id: f.ciclo_id } : {}),
    })),
  }), [nombre, sede, dias, indirecto, margen, inflacion, filas]);

  const simular = useMutation({
    mutationFn: () => catRpc<Res>('cat_licitacion_simular', { _params: params }),
    onSuccess: (r) => setRes(r), onError: (e) => toast.error(getErrorMessage(e)),
  });
  const guardar = useMutation({
    mutationFn: () => catRpc<{ resultado: Res }>('cat_licitacion_guardar', { _params: params, _id: editId }),
    onSuccess: (r) => { toast.success('Licitación guardada'); setRes(r.resultado); void qc.invalidateQueries({ queryKey: ['cat-licitaciones'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const cargar = useMutation({
    mutationFn: (id: string) => catRpc<any>('cat_licitacion_obtener', { _id: id }),
    onSuccess: (d) => {
      const p = d.params; setEditId(d.id); setNombre(p.nombre ?? ''); setSede(p.sede ?? ''); setDias(String(p.dias ?? 30));
      setIndirecto(String(p.indirecto_racion ?? 0)); setMargen(String(p.margen_objetivo_pct ?? 0)); setInflacion(String(p.inflacion_pct ?? 0));
      setFilas((p.servicios ?? []).map((s: any) => ({ servicio: s.servicio, pax: String(s.pax ?? ''), ciclo_id: s.ciclo_id ?? '', precio: String(s.precio ?? '') })));
      setRes(d.resultado);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const setFila = (i: number, k: keyof Fila, v: string) => setFilas((fs) => fs.map((f, j) => j === i ? { ...f, [k]: v } : f));

  return (
    <div className="grid grid-cols-1 gap-4 p-4 xl:grid-cols-5">
      {/* Formulario */}
      <Card className="xl:col-span-2">
        <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-base"><Calculator className="h-4 w-4" /> Pliego a cotizar</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div className="col-span-2"><label className="block text-xs text-muted-foreground">Nombre de la licitación *</label><input className={`${fieldCls} w-full`} value={nombre} onChange={(e) => setNombre(e.target.value)} /></div>
            <div className="col-span-2"><label className="block text-xs text-muted-foreground">Sede / cliente</label><input className={`${fieldCls} w-full`} value={sede} onChange={(e) => setSede(e.target.value)} /></div>
            <div><label className="block text-xs text-muted-foreground">Días del período</label><input className={fieldCls} type="number" value={dias} onChange={(e) => setDias(e.target.value)} /></div>
            <div><label className="block text-xs text-muted-foreground">Indirecto/ración ($)</label><input className={fieldCls} type="number" step="0.01" value={indirecto} onChange={(e) => setIndirecto(e.target.value)} /></div>
            <div><label className="block text-xs text-muted-foreground">Margen objetivo (%)</label><input className={fieldCls} type="number" value={margen} onChange={(e) => setMargen(e.target.value)} /></div>
            <div><label className="block text-xs text-muted-foreground">Colchón inflación (%)</label><input className={fieldCls} type="number" value={inflacion} onChange={(e) => setInflacion(e.target.value)} /></div>
          </div>
          <p className="pt-1 text-xs font-medium">Servicios</p>
          {filas.map((f, i) => (
            <div key={i} className="flex items-end gap-1">
              <select className={`${fieldCls} w-28`} value={f.servicio} onChange={(e) => setFila(i, 'servicio', e.target.value)}>
                {SERVICIOS.map((s) => <option key={s} value={s}>{SERVICIO_LABEL[s] ?? s}</option>)}
              </select>
              <input className={`${fieldCls} w-16`} type="number" placeholder="pax" value={f.pax} onChange={(e) => setFila(i, 'pax', e.target.value)} />
              <select className={`${fieldCls} w-24`} value={f.ciclo_id} onChange={(e) => setFila(i, 'ciclo_id', e.target.value)}>
                <option value="">costo…</option>
                {ciclos.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
              </select>
              <input className={`${fieldCls} w-20`} type="number" step="0.01" placeholder="precio" value={f.precio} onChange={(e) => setFila(i, 'precio', e.target.value)} />
              <button className="pb-2 text-red-600" onClick={() => setFilas((fs) => fs.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
          <Button size="sm" variant="ghost" onClick={() => setFilas((fs) => [...fs, { servicio: 'almuerzo_1', pax: '', ciclo_id: '', precio: '' }])}><Plus className="mr-1 h-3.5 w-3.5" /> Servicio</Button>
          <p className="text-[11px] text-muted-foreground">Elige un ciclo para tomar el food cost REAL de sus recetas, o déjalo vacío y el costo se asume 0 (cotización solo con indirecto).</p>
          <div className="flex gap-2 pt-1">
            <Button disabled={!params.servicios.length || simular.isPending} onClick={() => simular.mutate()}>Simular</Button>
            <Button variant="outline" disabled={!nombre || guardar.isPending} onClick={() => guardar.mutate()}><Save className="mr-1 h-4 w-4" /> {editId ? 'Actualizar' : 'Guardar'}</Button>
            {editId && <Button variant="ghost" onClick={() => { setEditId(null); setNombre(''); setSede(''); setFilas([{ servicio: 'almuerzo_1', pax: '', ciclo_id: '', precio: '' }]); setRes(null); }}>Nueva</Button>}
          </div>

          {guardadas.length > 0 && (
            <div className="pt-2">
              <p className="text-xs font-medium text-muted-foreground">Guardadas</p>
              {guardadas.map((l) => (
                <button key={l.id} onClick={() => cargar.mutate(l.id)} className="flex w-full items-center justify-between rounded px-2 py-1 text-left text-xs hover:bg-accent">
                  <span>{l.codigo} · {l.nombre}{l.sede ? ` · ${l.sede}` : ''}</span>
                  <span className={`font-mono ${Number(l.margen_pct) < 20 ? 'text-amber-600' : 'text-green-700 dark:text-green-400'}`}>{l.margen_pct}%</span>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Resultados */}
      <Card className="xl:col-span-3">
        <CardHeader className="pb-2"><CardTitle className="text-base">Resultado de la simulación</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {!res ? <p className="text-sm text-muted-foreground">Completa el pliego y pulsa Simular.</p> : (
            <>
              {/* KPIs */}
              <div>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  <div className="rounded border p-2"><p className="text-[11px] text-muted-foreground">Ingreso/día</p><p className="font-mono font-semibold">{money(res.totales_dia.ingreso, 2)}</p></div>
                  <div className="rounded border p-2"><p className="text-[11px] text-muted-foreground">Margen/día</p><p className={`font-mono font-semibold ${(res.totales_dia.margen_pct ?? 0) < 20 ? 'text-amber-600' : 'text-green-700 dark:text-green-400'}`}>{money(res.totales_dia.margen, 2)} ({res.totales_dia.margen_pct}%)</p></div>
                  <div className="rounded border p-2"><p className="text-[11px] text-muted-foreground">Food cost</p><p className="font-mono font-semibold">{res.totales_dia.food_cost_pct}%</p></div>
                  <div className="rounded border border-emerald-400 p-2"><p className="text-[11px] text-muted-foreground">Aguanta inflación hasta</p><p className="font-mono font-semibold text-emerald-700 dark:text-emerald-400">+{res.totales_dia.colchon_inflacion_pct}%</p></div>
                </div>
                <button className="mt-1 flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground" onClick={() => setKpiOpen((v) => !v)}>
                  {kpiOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />} Cómo se calculan estos totales
                </button>
                {kpiOpen && <MemoriaTotales t={res.totales_dia} />}
              </div>

              {/* Por servicio */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="py-1 pr-2">Servicio</th><th className="py-1 pr-2 text-right">Costo/rac</th><th className="py-1 pr-2 text-right">Precio</th>
                    <th className="py-1 pr-2 text-right">Margen</th><th className="py-1 pr-2 text-right" title="Precio mínimo para el margen objetivo bajo el colchón de inflación"><ShieldCheck className="ml-auto h-3.5 w-3.5" /></th>
                    <th className="py-1 pr-2 text-right">Colchón</th>
                  </tr></thead>
                  <tbody>
                    {res.por_servicio.map((s) => {
                      const open = abierto === s.servicio;
                      return (
                      <Fragment key={s.servicio}>
                      <tr className="cursor-pointer border-b last:border-0 hover:bg-accent/40" onClick={() => setAbierto(open ? null : s.servicio)}>
                        <td className="py-1 pr-2">
                          <span className="flex items-center gap-1">
                            {open ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
                            {SERVICIO_LABEL[s.servicio] ?? s.servicio}
                          </span>
                          <span className="block pl-4 text-[11px] text-muted-foreground">{int0(s.pax)} pax · fc {s.food_cost_pct}%</span>
                        </td>
                        <td className="py-1 pr-2 text-right font-mono">{money(s.costo_total_racion, 4)}</td>
                        <td className="py-1 pr-2 text-right font-mono">{money(s.precio, 2)}</td>
                        <td className={`py-1 pr-2 text-right font-mono ${(s.margen_pct ?? 0) < 20 ? 'text-amber-600' : ''}`}>{s.margen_pct}%</td>
                        <td className="py-1 pr-2 text-right font-mono text-emerald-700 dark:text-emerald-400" title="Precio garantizado">{s.precio_garantizado != null ? money(s.precio_garantizado, 2) : '—'}</td>
                        <td className="py-1 pr-2 text-right font-mono">{s.colchon_inflacion_pct != null ? `+${s.colchon_inflacion_pct}%` : '—'}</td>
                      </tr>
                      {open && (
                        <tr className="border-b last:border-0">
                          <td colSpan={6} className="px-2 pb-3 pt-0">
                            <MemoriaCalculo s={s} margenObj={res.recomendacion.margen_objetivo_pct} infl={res.recomendacion.inflacion_colchon_pct} />
                          </td>
                        </tr>
                      )}
                      </Fragment>
                      );
                    })}
                  </tbody>
                </table>
                <p className="pt-1 text-[11px] text-muted-foreground">Toca una fila para ver <b>cómo se calcula cada columna</b> con sus números.</p>
                <p className="pt-0.5 text-[11px] text-muted-foreground"><ShieldCheck className="mr-1 inline h-3 w-3 text-emerald-600" />Precio garantizado: el mínimo a cotizar para asegurar {res.recomendacion.margen_objetivo_pct}% de margen aun con +{res.recomendacion.inflacion_colchon_pct}% de inflación de insumos.</p>
              </div>

              {/* Escenarios */}
              <div>
                <p className="flex items-center gap-1 pb-1 text-sm font-medium"><TrendingDown className="h-4 w-4" /> Margen del día bajo inflación de insumos (al precio propuesto)</p>
                <div className="flex flex-wrap gap-2">
                  {res.escenarios.map((e) => (
                    <div key={e.inflacion_pct} className={`rounded-md border px-3 py-1 text-sm ${(e.margen_pct ?? 0) < 0 ? 'border-red-400 bg-red-50 dark:bg-red-900/20' : (e.margen_pct ?? 0) < 15 ? 'border-amber-400' : ''}`}>
                      <span className="text-xs text-muted-foreground">+{int0(e.inflacion_pct)}% insumos</span>
                      <p className="font-mono">{money(e.margen_dia, 0)} <span className="text-xs">({e.margen_pct}%)</span></p>
                    </div>
                  ))}
                </div>
                <button className="mt-1 flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground" onClick={() => setEscOpen((v) => !v)}>
                  {escOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />} Cómo se calcula cada escenario
                </button>
                {escOpen && <MemoriaEscenarios res={res} />}
              </div>

              <p className="text-xs text-muted-foreground">Período de {res.totales_periodo.dias} días: ingreso {money(res.totales_periodo.ingreso, 2)} · costo {money(res.totales_periodo.costo_total, 2)} · <b>margen {money(res.totales_periodo.margen, 2)}</b>.</p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
const int0 = (v: number) => num(v, 0);

// Memoria de cálculo por servicio: reconstruye cada columna con los números reales.
// El "=" muestra el valor exacto que devolvió la RPC; la operación usa los insumos de la fila.
function MemoriaCalculo({ s, margenObj, infl }: { s: SrvRes; margenObj: number; infl: number }) {
  const cmp = num(s.costo_mp_racion, 2);       // materia prima (food cost)
  const ind = num(s.indirecto_racion, 2);      // costo indirecto por ración
  const costo = num(s.costo_total_racion, 2);  // CMP + indirecto
  const precio = num(s.precio, 2);
  const margen = num(s.margen_racion, 2);      // precio − costo
  const factorInfl = num(1 + infl / 100, 2);   // p. ej. 1,12
  const divisor = num(1 - margenObj / 100, 2); // 1 − margen objetivo, p. ej. 0,78
  return (
    <div className="space-y-1 rounded-md bg-muted/50 p-3 text-[11.5px]">
      <p className="font-medium text-muted-foreground">Cómo se calcula — por ración</p>
      <div className="grid grid-cols-1 gap-x-6 gap-y-1 sm:grid-cols-2">
        <Formula etiqueta="Costo/ración" op={`MP $${cmp} + indirecto $${ind}`} res={`$${costo}`} />
        <Formula etiqueta="Food cost" op={`MP $${cmp} ÷ precio $${precio}`} res={`${s.food_cost_pct ?? '—'}%`} />
        <Formula etiqueta="Margen" op={`precio $${precio} − costo $${costo} = $${margen}`} res={`${s.margen_pct ?? '—'}%`} nota={`$${margen} ÷ $${precio}`} />
        <Formula etiqueta="Aguanta inflación" op={`margen $${margen} ÷ MP $${cmp}`} res={s.colchon_inflacion_pct != null ? `+${s.colchon_inflacion_pct}%` : '—'} nota="cuánto pueden subir los insumos antes de margen $0" />
        <Formula etiqueta="Precio mínimo" op={`(MP $${cmp} × ${factorInfl} + indirecto $${ind}) ÷ (1 − ${num(margenObj / 100, 2)})`} res={s.precio_garantizado != null ? `$${num(s.precio_garantizado, 2)}` : '—'} nota={`asegura ${num(margenObj, 0)}% de margen aun con +${num(infl, 0)}% de inflación · divisor = ${divisor}`} className="sm:col-span-2" />
      </div>
    </div>
  );
}
function Formula({ etiqueta, op, res, nota, className = '' }: { etiqueta: string; op: string; res: string; nota?: string; className?: string }) {
  return (
    <div className={`flex flex-wrap items-baseline gap-x-1.5 ${className}`}>
      <span className="w-28 shrink-0 text-muted-foreground">{etiqueta}</span>
      <span className="font-mono">{op}</span>
      {res && <span className="font-mono font-semibold text-foreground">= {res}</span>}
      {nota && <span className="block w-full pl-28 text-[10.5px] text-muted-foreground">{nota}</span>}
    </div>
  );
}

// Desglose de los totales del día: cada KPI se arma sumando los servicios.
function MemoriaTotales({ t }: { t: Res['totales_dia'] }) {
  const ingreso = num(t.ingreso, 2), mp = num(t.costo_mp, 2), costo = num(t.costo_total, 2), margen = num(t.margen, 2);
  return (
    <div className="mt-1 space-y-1 rounded-md bg-muted/50 p-3 text-[11.5px]">
      <p className="font-medium text-muted-foreground">Cómo se calculan — total del día (suma de todos los servicios)</p>
      <div className="grid grid-cols-1 gap-x-6 gap-y-1 sm:grid-cols-2">
        <Formula etiqueta="Ingreso/día" op="Σ (precio × pax) de cada servicio" res={`$${ingreso}`} />
        <Formula etiqueta="Costo MP/día" op="Σ (materia prima × pax)" res={`$${mp}`} />
        <Formula etiqueta="Costo total/día" op="Σ (costo/ración × pax)" res={`$${costo}`} nota="materia prima + indirecto" />
        <Formula etiqueta="Margen/día" op={`ingreso $${ingreso} − costo $${costo}`} res={`$${margen}`} nota={`= ${t.margen_pct ?? '—'}%  ($${margen} ÷ $${ingreso})`} />
        <Formula etiqueta="Food cost" op={`costo MP $${mp} ÷ ingreso $${ingreso}`} res={`${t.food_cost_pct ?? '—'}%`} />
        <Formula etiqueta="Aguanta inflación" op={`margen $${margen} ÷ costo MP $${mp}`} res={t.colchon_inflacion_pct != null ? `+${t.colchon_inflacion_pct}%` : '—'} nota="cuánto pueden subir los insumos antes de margen $0" />
      </div>
    </div>
  );
}

// Desglose de escenarios: solo la materia prima se infla; el indirecto queda fijo.
function MemoriaEscenarios({ res }: { res: Res }) {
  const t = res.totales_dia;
  const indDia = t.costo_total - t.costo_mp; // parte fija (indirecto) del costo del día
  const ejemplo = [...res.escenarios].filter((e) => e.inflacion_pct > 0).sort((a, b) => b.inflacion_pct - a.inflacion_pct)[0] ?? res.escenarios[res.escenarios.length - 1];
  const mp = num(t.costo_mp, 2), ind = num(indDia, 2), ingreso = num(t.ingreso, 2);
  const factor = ejemplo ? num(1 + ejemplo.inflacion_pct / 100, 2) : '—';
  return (
    <div className="mt-1 space-y-1 rounded-md bg-muted/50 p-3 text-[11.5px]">
      <p className="font-medium text-muted-foreground">Cómo se calcula cada escenario</p>
      <p className="text-muted-foreground">Solo sube la <b>materia prima</b> (costo MP {`$${mp}`}); el <b>indirecto</b> del día queda fijo en {`$${ind}`}. El precio de venta no cambia.</p>
      <div className="grid grid-cols-1 gap-x-6 gap-y-1">
        <Formula etiqueta="Costo del día" op={`costo MP × (1 + inflación) + indirecto $${ind}`} res="" />
        <Formula etiqueta="Margen del día" op={`ingreso $${ingreso} − costo del día`} res="" />
      </div>
      {ejemplo && (
        <div className="grid grid-cols-1 gap-x-6 gap-y-1 border-t pt-1">
          <p className="text-muted-foreground">Ejemplo · +{int0(ejemplo.inflacion_pct)}% de inflación de insumos:</p>
          <Formula etiqueta="Costo del día" op={`$${mp} × ${factor} + $${ind}`} res={`$${num(ejemplo.costo_dia, 2)}`} />
          <Formula etiqueta="Margen del día" op={`$${ingreso} − $${num(ejemplo.costo_dia, 2)}`} res={`$${num(ejemplo.margen_dia, 2)} (${ejemplo.margen_pct ?? '—'}%)`} />
        </div>
      )}
    </div>
  );
}
